import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import buildGroupInfoDto from "../../utils/ratings/buildGroupInfoDto";
import buildRatingMap from "../../utils/ratings/buildRatingMap";
import buildRatingPayload from "../../utils/ratings/buildRatingPayload";

/**
 * Handles the ratings save path for both HOMEROOM and GROUPS modes.
 *
 * Responsibilities:
 * - Bridges useRatingState's flat ratings object → buildRatingPayload (via buildRatingMap)
 * - Builds the correct DAO options for HOMEROOM vs GROUPS
 * - After a GROUPS save, refreshes group ratings (unless skipRefresh or the
 *   teacher switched subjects). Does not re-fetch subject comments/fluency —
 *   that path has its own save/sync and a post-ratings comments refresh would
 *   rehydrate stale WPM over unsaved fluency edits.
 * - Guards against stale-template responses (teacher switches subjects mid-flight)
 * - Accepts `{ skipRefresh: true }` so subject-switch flush can persist ratings
 *   without kicking off a GROUPS re-fetch that would race the next subject
 * - Exposes a stable `saveRatings` callback
 *
 * @param {Object}      params
 * @param {"HOMEROOM"|"GROUPS"} params.mode
 * @param {Object}      params.ratings                            - from useRatingState
 * @param {Array}       params.contentItems                       - [{templateContentId, contentType}]
 * @param {Array}       params.existingRatingsData                - server state
 * @param {Object|null} params.groupContext                       - GroupContext (GROUPS) or null (HOMEROOM)
 * @param {Object|null} params.selectedEpr                       - own EPR or null
 * @param {Object|null} params.selectedTemplate                  - {templateId}
 * @param {Object}      params.userDetails                       - {uid}
 * @param {string}      params.token
 * @param {Function}    params.setHasChanges                     - from useRatingState
 * @param {Function}    [params.setRatingsLoaded]                - from useRatingState; disables cells during save
 * @param {Function}    params.setExistingRatingsData             - updates server baseline after save
 * @param {Function}    [params.fetchSelectedGroupInfo]          - GROUPS only: re-fetches group data
 *
 * @returns {{ saveRatings: Function }}
 */
const useRatingSave = ({
    mode,
    ratings,
    contentItems,
    existingRatingsData,
    groupContext,
    selectedEpr,
    selectedTemplate,
    userDetails,
    token,
    setHasChanges,
    setRatingsLoaded,
    setExistingRatingsData,
    fetchSelectedGroupInfo
}) => {
    // Ref-based getter so the response handler can compare templateId at
    // response time without it in the dependency array (avoids stale closures).
    const selectedTemplateIdRef = useRef(selectedTemplate?.templateId);
    useEffect(() => {
        selectedTemplateIdRef.current = selectedTemplate?.templateId;
    }, [selectedTemplate?.templateId]);

    const saveRatings = useCallback(
        async (options = {}) => {
            if (!selectedTemplate || !token) return;

            const skipRefresh = options?.skipRefresh === true;
            const templateIdAtSubmit = selectedTemplate.templateId;
            const raterGuid = userDetails?.uid ?? null;

            const ratingMap = buildRatingMap({
                ratings,
                contentItems,
                existingRatingsData,
                groupContext,
                selectedEpr,
                templateId: templateIdAtSubmit
            });

            const payload = buildRatingPayload({ ratingMap, raterGuid });

            if (payload.length === 0) {
                toast.info("There are no ratings to save.", { autoClose: 2000 });
                setHasChanges(false);
                return;
            }

            if (setRatingsLoaded) setRatingsLoaded(false);

            try {
                let response;

                if (mode === "GROUPS") {
                    if (!groupContext) {
                        toast.error("Group context is not available. Please reload and try again.");
                        return;
                    }

                    const groupInfoDto = buildGroupInfoDto({
                        ownProfile: groupContext.ownProfile,
                        sharedGroups: groupContext.sharedGroups
                    });

                    response = await ElementaryProgressReportDao({
                        action: "updateGroupContentRatings",
                        data: { groupInfoDto, contentRatings: payload },
                        token
                    });

                    if (response?.data?.payload) {
                        setHasChanges(false);
                        toast.success("Ratings successfully saved.", { autoClose: 3000 });

                        // Refresh group ratings after a normal save. Skip when the
                        // caller is about to tear down this subject's state (subject
                        // switch), or when the teacher already switched templates
                        // mid-flight. Subject comments/fluency are not refreshed here.
                        const templateStillCurrent = selectedTemplateIdRef.current === templateIdAtSubmit;
                        if (!skipRefresh && templateStillCurrent) {
                            if (fetchSelectedGroupInfo) fetchSelectedGroupInfo();
                        }
                    } else {
                        toast.error("Ratings were not saved. Please try again later.", { autoClose: 3000 });
                    }
                } else {
                    // HOMEROOM — gradingProfileId and gradingCriteriaId required
                    if (!selectedEpr) {
                        toast.error("No grading profile found. Please reload and try again.");
                        return;
                    }

                    const criteria = selectedEpr.gradingCriteriaList?.find(
                        (c) => Number.parseInt(c.parentTemplate, 10) === templateIdAtSubmit
                    );

                    if (!criteria) {
                        toast.error("Grading criteria not found. Please reload and try again.");
                        return;
                    }

                    response = await ElementaryProgressReportDao({
                        action: "updateContentRatings",
                        gradingProfileId: String(selectedEpr.key),
                        gradingCriteriaId: String(criteria.key),
                        data: payload,
                        token
                    });

                    if (response?.data?.payload) {
                        // Only update server baseline if teacher hasn't switched subjects
                        // mid-flight. If they have, discard — the new subject owns state now.
                        if (selectedTemplateIdRef.current === templateIdAtSubmit) {
                            setExistingRatingsData(response.data.payload);
                        }
                        setHasChanges(false);
                        toast.success("Ratings successfully saved.", { autoClose: 3000 });
                    } else {
                        toast.error("Ratings were not saved. Please try again later.", { autoClose: 3000 });
                    }
                }
            } catch (err) {
                console.error("useRatingSave: saveRatings failed:", err);
                toast.error("Ratings were not saved. Please try again later.");
            } finally {
                if (setRatingsLoaded) setRatingsLoaded(true);
            }
        },
        [
            mode,
            ratings,
            contentItems,
            existingRatingsData,
            groupContext,
            selectedEpr,
            selectedTemplate,
            userDetails,
            token,
            setHasChanges,
            setRatingsLoaded,
            setExistingRatingsData,
            fetchSelectedGroupInfo
        ]
    );

    return { saveRatings };
};

export default useRatingSave;
